// react
import { useContext, useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextSpendPlan } from '../ProjectV2.context';

// mui
import {
	alpha,
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Grid,
	IconButton,
	InputAdornment,
	LinearProgress,
	Paper,
	Stack,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AccountBalance as IconBank,
	AccountBalanceWallet as IconWallet,
	ArrowUpward as IconArrowUp,
	CheckCircle as IconVerified,
	Close as IconClose,
	CreditCard as IconCard,
	Download as IconDownload,
	FilterList as IconFilter,
	HourglassEmpty as IconPending,
	OpenInNew as IconOpen,
	Payments as IconPayments,
	Receipt as IconReceipt,
	Search as IconSearch,
	Timeline as IconTimeline,
} from '@mui/icons-material';

// recharts
import {
	Area,
	AreaChart,
	CartesianGrid,
	Cell,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from 'recharts';

// components
import { PaginationBar } from '../../../ui/SearchFilterBar/SearchFilterBar';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// ui
import { Empty } from '../../../ui/Empty/Empty';

// utils
import { formatCurrency } from '../../../../utils/currency';

// Types

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

// Tab Panel Component

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`payment-report-tabpanel-${index}`}
			aria-labelledby={`payment-report-tab-${index}`}
			{...other}>
			{value === index && <Box sx={{ py: 3 }}>{children}</Box>}
		</div>
	);
};

// Payment Report Component

export const PaymentReport = () => {
	// hooks
	const { t } = useTranslation();
	const theme = useTheme();

	// context
	const { activities, contracts, vouchers, workPlans } = useContext(ContextSpendPlan);

	// state
	const [tabValue, setTabValue] = useState(0);
	const [searchQuery, setSearchQuery] = useState('');
	const [page, setPage] = useState(1);
	const rowsPerPage = 10;

	// Get default currency from project
	const defaultCurrency = activities[0]?.currency || 'USD';

	// handlers
	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setTabValue(newValue);
	};

	// Enriched voucher data with related information
	const enrichedVouchers = useMemo(() => {
		return vouchers.map((voucher) => {
			const contractObj = typeof voucher.contract === 'object' ? voucher.contract : null;
			const contractId = typeof voucher.contract === 'number' ? voucher.contract : contractObj?.id;
			const contract = contracts.find((c) => c.id === contractId);

			const activityId = contract?.planningActivity;
			const activity = activities.find((a) => a.id === activityId);

			const workPlan = activity?.workPlan
				? typeof activity.workPlan === 'object'
					? activity.workPlan
					: workPlans.find((wp) => wp.id === activity.workPlan)
				: null;

			// Mock payout channel - in real implementation this would come from the voucher data
			const payoutChannels = ['Bank Transfer', 'Mobile Money', 'Digital Wallet', 'Card Payment'];
			const payoutChannel = payoutChannels[Math.floor(Math.random() * payoutChannels.length)];

			return {
				...voucher,
				contract,
				activity,
				workPlan,
				payoutChannel,
			};
		});
	}, [vouchers, contracts, activities, workPlans]);

	// Filtered vouchers
	const filteredVouchers = useMemo(() => {
		if (!searchQuery) return enrichedVouchers;
		const query = searchQuery.toLowerCase();
		return enrichedVouchers.filter(
			(voucher) =>
				voucher.activity?.name.toLowerCase().includes(query) ||
				voucher.contract?.num?.toLowerCase().includes(query) ||
				voucher.payoutChannel.toLowerCase().includes(query)
		);
	}, [enrichedVouchers, searchQuery]);

	// Paginated vouchers
	const paginatedVouchers = useMemo(() => {
		const startIndex = (page - 1) * rowsPerPage;
		return filteredVouchers.slice(startIndex, startIndex + rowsPerPage);
	}, [filteredVouchers, page, rowsPerPage]);

	// Summary statistics
	const summaryStats = useMemo(() => {
		const totalPayments = vouchers.length;
		const totalAmount = vouchers.reduce((sum, v) => sum + (v.amount || 0), 0);
		const verifiedPayments = vouchers.filter(
			(v) => v.status === 'VERIFIED' || v.status === 'REDEEMED'
		).length;
		const pendingPayments = vouchers.filter(
			(v) => v.status === 'READY' || v.status === 'GENERATED'
		).length;
		const verifiedAmount = vouchers
			.filter((v) => v.status === 'VERIFIED' || v.status === 'REDEEMED')
			.reduce((sum, v) => sum + (v.amount || 0), 0);

		return {
			totalPayments,
			totalAmount,
			verifiedPayments,
			pendingPayments,
			verifiedAmount,
		};
	}, [vouchers]);

	// Payment trend data
	const paymentTrendData = useMemo(() => {
		const monthlyData: Record<string, { month: string; amount: number; count: number }> = {};

		enrichedVouchers.forEach((voucher) => {
			const date = voucher.created ? new Date(voucher.created) : new Date();
			const monthKey = date.toISOString().slice(0, 7);
			const monthLabel = date.toLocaleDateString('en-US', {
				month: 'short',
				year: '2-digit',
				timeZone: 'UTC',
			});

			if (!monthlyData[monthKey]) {
				monthlyData[monthKey] = { month: monthLabel, amount: 0, count: 0 };
			}
			monthlyData[monthKey].amount += voucher.amount || 0;
			monthlyData[monthKey].count += 1;
		});

		return Object.values(monthlyData)
			.sort((a, b) => a.month.localeCompare(b.month))
			.slice(-12);
	}, [enrichedVouchers]);

	// Payment status distribution
	const statusDistribution = useMemo(() => {
		const distribution: Record<string, number> = {};
		vouchers.forEach((voucher) => {
			const status = voucher.status || 'UNKNOWN';
			distribution[status] = (distribution[status] || 0) + 1;
		});
		return Object.entries(distribution).map(([name, value]) => ({ name, value }));
	}, [vouchers]);

	// Payout channel distribution
	const channelDistribution = useMemo(() => {
		const distribution: Record<string, { count: number; amount: number }> = {};
		enrichedVouchers.forEach((voucher) => {
			const channel = voucher.payoutChannel;
			if (!distribution[channel]) {
				distribution[channel] = { count: 0, amount: 0 };
			}
			distribution[channel].count += 1;
			distribution[channel].amount += voucher.amount || 0;
		});
		return Object.entries(distribution).map(([name, data]) => ({
			name,
			count: data.count,
			amount: data.amount,
		}));
	}, [enrichedVouchers]);

	// Status color mapping
	const getStatusColor = (status: string) => {
		switch (status) {
			case 'VERIFIED':
			case 'REDEEMED':
				return theme.palette.success.main;
			case 'READY':
				return theme.palette.info.main;
			case 'GENERATED':
				return theme.palette.warning.main;
			case 'CANCELLED':
				return theme.palette.error.main;
			default:
				return theme.palette.grey[500];
		}
	};

	const getStatusLabel = (status: string, t: (key: string) => string) => {
		switch (status) {
			case 'VERIFIED':
				return t('verified');
			case 'REDEEMED':
				return t('redeemed');
			case 'READY':
				return t('ready');
			case 'GENERATED':
				return t('generated');
			case 'CANCELLED':
				return t('cancelled');
			default:
				return status;
		}
	};

	const getChannelIcon = (channel: string) => {
		switch (channel) {
			case 'Bank Transfer':
				return <IconBank sx={{ fontSize: 20 }} />;
			case 'Mobile Money':
				return <IconWallet sx={{ fontSize: 20 }} />;
			case 'Digital Wallet':
				return <IconWallet sx={{ fontSize: 20 }} />;
			case 'Card Payment':
				return <IconCard sx={{ fontSize: 20 }} />;
			default:
				return <IconPayments sx={{ fontSize: 20 }} />;
		}
	};

	// Render summary cards
	const renderSummaryCards = () => (
		<Grid container spacing={3} sx={{ mb: 4 }}>
			{/* Total Payments Card */}
			<Grid item xs={12} sm={6} md={3}>
				<Card
					sx={{
						height: '100%',
						borderRadius: 3,
						border: `1px solid ${theme.palette.divider}`,
					}}>
					<CardContent sx={{ p: 2.5 }}>
						<Stack direction="row" alignItems="center" justifyContent="space-between">
							<Box>
								<Typography variant="caption" color="text.secondary" fontWeight={500}>
									{t('spendPlan.paymentReport.totalPayments')}
								</Typography>
								<Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
									{summaryStats.totalPayments}
								</Typography>
							</Box>
							<Avatar
								sx={{
									width: 48,
									height: 48,
									bgcolor: alpha(theme.palette.primary.main, 0.1),
								}}>
								<IconReceipt sx={{ color: theme.palette.primary.main }} />
							</Avatar>
						</Stack>
						<Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 2 }}>
							<IconArrowUp sx={{ fontSize: 16, color: theme.palette.success.main }} />
							<Typography variant="caption" color="success.main" fontWeight={500}>
								+12%
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{t('spendPlan.paymentReport.fromLastMonth')}
							</Typography>
						</Stack>
					</CardContent>
				</Card>
			</Grid>

			{/* Total Amount Card */}
			<Grid item xs={12} sm={6} md={3}>
				<Card
					sx={{
						height: '100%',
						borderRadius: 3,
						border: `1px solid ${theme.palette.divider}`,
					}}>
					<CardContent sx={{ p: 2.5 }}>
						<Stack direction="row" alignItems="center" justifyContent="space-between">
							<Box>
								<Typography variant="caption" color="text.secondary" fontWeight={500}>
									{t('spendPlan.paymentReport.totalAmount')}
								</Typography>
								<Typography variant="h4" fontWeight={700} color="primary.main" sx={{ mt: 0.5 }}>
									{formatCurrency({ value: summaryStats.totalAmount, currency: defaultCurrency })}
								</Typography>
							</Box>
							<Avatar
								sx={{
									width: 48,
									height: 48,
									bgcolor: alpha(theme.palette.primary.main, 0.1),
								}}>
								<IconPayments sx={{ color: theme.palette.primary.main }} />
							</Avatar>
						</Stack>
						<Box sx={{ mt: 2 }}>
							<LinearProgress
								variant="determinate"
								value={75}
								sx={{
									height: 6,
									borderRadius: 3,
									bgcolor: alpha(theme.palette.primary.main, 0.1),
									'& .MuiLinearProgress-bar': {
										borderRadius: 3,
										bgcolor: theme.palette.primary.main,
									},
								}}
							/>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.5, display: 'block' }}>
								75% of budget utilized
							</Typography>
						</Box>
					</CardContent>
				</Card>
			</Grid>

			{/* Verified Payments Card */}
			<Grid item xs={12} sm={6} md={3}>
				<Card
					sx={{
						height: '100%',
						borderRadius: 3,
						border: `1px solid ${theme.palette.divider}`,
					}}>
					<CardContent sx={{ p: 2.5 }}>
						<Stack direction="row" alignItems="center" justifyContent="space-between">
							<Box>
								<Typography variant="caption" color="text.secondary" fontWeight={500}>
									{t('spendPlan.paymentReport.verifiedPayments')}
								</Typography>
								<Typography variant="h4" fontWeight={700} color="success.main" sx={{ mt: 0.5 }}>
									{summaryStats.verifiedPayments}
								</Typography>
							</Box>
							<Avatar
								sx={{
									width: 48,
									height: 48,
									bgcolor: alpha(theme.palette.success.main, 0.1),
								}}>
								<IconVerified sx={{ color: theme.palette.success.main }} />
							</Avatar>
						</Stack>
						<Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
							{formatCurrency({ value: summaryStats.verifiedAmount, currency: defaultCurrency })}{' '}
							{t('spendPlan.paymentReport.verified')}
						</Typography>
					</CardContent>
				</Card>
			</Grid>

			{/* Pending Payments Card */}
			<Grid item xs={12} sm={6} md={3}>
				<Card
					sx={{
						height: '100%',
						borderRadius: 3,
						border: `1px solid ${theme.palette.divider}`,
					}}>
					<CardContent sx={{ p: 2.5 }}>
						<Stack direction="row" alignItems="center" justifyContent="space-between">
							<Box>
								<Typography variant="caption" color="text.secondary" fontWeight={500}>
									{t('spendPlan.paymentReport.pendingPayments')}
								</Typography>
								<Typography variant="h4" fontWeight={700} color="warning.main" sx={{ mt: 0.5 }}>
									{summaryStats.pendingPayments}
								</Typography>
							</Box>
							<Avatar
								sx={{
									width: 48,
									height: 48,
									bgcolor: alpha(theme.palette.warning.main, 0.1),
								}}>
								<IconPending sx={{ color: theme.palette.warning.main }} />
							</Avatar>
						</Stack>
						<Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
							{t('spendPlan.paymentReport.awaitingVerification')}
						</Typography>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);

	// Render payments list tab
	const renderPaymentsTab = () => (
		<Box>
			{/* Search and filters */}
			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
				<TextField
					placeholder={t('spendPlan.paymentReport.searchPlaceholder')}
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					size="small"
					sx={{
						minWidth: 300,
						'& .MuiOutlinedInput-root': {
							backgroundColor: 'background.paper',
							borderRadius: 2,
						},
					}}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<IconSearch sx={{ color: 'text.disabled', fontSize: 20 }} />
							</InputAdornment>
						),
						endAdornment: searchQuery ? (
							<InputAdornment position="end">
								<IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
									<IconClose sx={{ fontSize: 18 }} />
								</IconButton>
							</InputAdornment>
						) : null,
					}}
				/>
				<Stack direction="row" spacing={1}>
					<Button variant="outlined" startIcon={<IconFilter />} size="small">
						{t('spendPlan.paymentReport.filter')}
					</Button>
					<Button variant="outlined" startIcon={<IconDownload />} size="small">
						{t('spendPlan.paymentReport.export')}
					</Button>
				</Stack>
			</Stack>

			{/* Payment cards list - modern card style */}
			<Stack spacing={2}>
				{paginatedVouchers.length === 0 ? (
					<Paper sx={{ p: 4, borderRadius: 2 }}>
						<Empty
							IconCircleProps={{ icon: <IconPayments /> }}
							content={{ children: t('spendPlan.paymentReport.empty.description') }}
						/>
					</Paper>
				) : (
					paginatedVouchers.map((voucher) => (
						<Paper
							key={voucher.id}
							sx={{
								p: 2.5,
								borderRadius: 2,
								border: `1px solid ${theme.palette.divider}`,
								transition: 'all 0.2s ease',
								'&:hover': {
									borderColor: theme.palette.primary.main,
									boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
								},
							}}>
							<Stack direction="row" alignItems="center" justifyContent="space-between">
								{/* Left side - Payment info */}
								<Stack direction="row" alignItems="center" spacing={2.5}>
									<Avatar
										sx={{
											width: 44,
											height: 44,
											bgcolor: alpha(getStatusColor(voucher.status), 0.1),
										}}>
										{getChannelIcon(voucher.payoutChannel)}
									</Avatar>
									<Box>
										<Stack direction="row" alignItems="center" spacing={1}>
											<Typography variant="subtitle2" fontWeight={600}>
												{voucher.activity?.name || 'Unknown Activity'}
											</Typography>
											<Chip
												label={getStatusLabel(voucher.status, t)}
												size="small"
												sx={{
													height: 20,
													fontSize: '0.7rem',
													bgcolor: alpha(getStatusColor(voucher.status), 0.1),
													color: getStatusColor(voucher.status),
													fontWeight: 600,
												}}
											/>
										</Stack>
										<Typography variant="caption" color="text.secondary">
											{voucher.contract?.num || 'No contract'} •{' '}
											{voucher.created
												? new Date(voucher.created).toLocaleDateString('en-US', {
														month: 'short',
														day: 'numeric',
														year: 'numeric',
													})
												: '-'}
										</Typography>
									</Box>
								</Stack>

								{/* Middle - Payout channel */}
								<Stack alignItems="center" sx={{ minWidth: 120 }}>
									<Stack direction="row" alignItems="center" spacing={0.5}>
										{getChannelIcon(voucher.payoutChannel)}
										<Typography variant="caption" color="text.secondary">
											{voucher.payoutChannel}
										</Typography>
									</Stack>
								</Stack>

								{/* Right side - Amount */}
								<Stack alignItems="flex-end">
									<Typography
										variant="h6"
										fontWeight={700}
										color={
											voucher.status === 'VERIFIED' || voucher.status === 'REDEEMED'
												? 'success.main'
												: 'text.primary'
										}>
										{formatCurrency({
											value: voucher.amount || 0,
											currency: voucher.activity?.currency || defaultCurrency,
										})}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{voucher.workPlan?.name || '-'}
									</Typography>
								</Stack>

								{/* Action */}
								<Tooltip title={t('spendPlan.paymentReport.viewDetails')}>
									<IconButton size="small">
										<IconOpen sx={{ fontSize: 18 }} />
									</IconButton>
								</Tooltip>
							</Stack>
						</Paper>
					))
				)}
			</Stack>

			{/* Pagination */}
			{filteredVouchers.length > rowsPerPage && (
				<Box sx={{ mt: 3 }}>
					<PaginationBar
						totalCount={filteredVouchers.length}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={setPage}
						onRowsPerPageChange={() => {
							/** empty arrow */
						}}
					/>
				</Box>
			)}
		</Box>
	);

	// Render analytics tab
	const renderAnalyticsTab = () => (
		<Box>
			<Grid container spacing={3}>
				{/* Payment Trend Chart */}
				<Grid item xs={12} lg={8}>
					<Paper sx={{ p: 3, borderRadius: 2 }}>
						<Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
							{t('spendPlan.paymentReport.paymentTrend')}
						</Typography>
						<ResponsiveContainer width="100%" height={300}>
							<AreaChart data={paymentTrendData}>
								<CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
								<XAxis
									dataKey="month"
									tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
								/>
								<YAxis
									tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
									tickFormatter={(value) =>
										formatCurrency({ value, currency: defaultCurrency, notation: 'compact' })
									}
								/>
								<RechartsTooltip
									formatter={(value: number) =>
										formatCurrency({ value, currency: defaultCurrency })
									}
									contentStyle={{
										backgroundColor: theme.palette.background.paper,
										border: `1px solid ${theme.palette.divider}`,
										borderRadius: 8,
									}}
								/>
								<Area
									type="monotone"
									dataKey="amount"
									name={t('spendPlan.paymentReport.amount')}
									stroke={theme.palette.success.main}
									fill={alpha(theme.palette.success.main, 0.2)}
									strokeWidth={2}
								/>
							</AreaChart>
						</ResponsiveContainer>
					</Paper>
				</Grid>

				{/* Status Distribution */}
				<Grid item xs={12} lg={4}>
					<Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
						<Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
							{t('spendPlan.paymentReport.statusDistribution')}
						</Typography>
						<ResponsiveContainer width="100%" height={250}>
							<PieChart>
								<Pie
									data={statusDistribution}
									cx="50%"
									cy="50%"
									innerRadius={50}
									outerRadius={80}
									paddingAngle={2}
									dataKey="value">
									{statusDistribution.map((entry, index) => (
										<Cell key={`cell-${index}`} fill={getStatusColor(entry.name)} />
									))}
								</Pie>
								<RechartsTooltip
									formatter={(value: number, name: string) => [value, getStatusLabel(name, t)]}
									contentStyle={{
										backgroundColor: theme.palette.background.paper,
										border: `1px solid ${theme.palette.divider}`,
										borderRadius: 8,
									}}
								/>
								<Legend
									formatter={(value) => getStatusLabel(value, t)}
									wrapperStyle={{ fontSize: 12 }}
								/>
							</PieChart>
						</ResponsiveContainer>
					</Paper>
				</Grid>

				{/* Payout Channels */}
				<Grid item xs={12}>
					<Paper sx={{ p: 3, borderRadius: 2 }}>
						<Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
							{t('spendPlan.paymentReport.payoutChannels')}
						</Typography>
						<Grid container spacing={2}>
							{channelDistribution.map((channel) => (
								<Grid item xs={12} sm={6} md={3} key={channel.name}>
									<Card
										sx={{
											p: 2,
											bgcolor: alpha(theme.palette.primary.main, 0.04),
											border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
											borderRadius: 2,
										}}>
										<Stack direction="row" alignItems="center" spacing={1.5}>
											<Avatar
												sx={{
													width: 40,
													height: 40,
													bgcolor: alpha(theme.palette.primary.main, 0.1),
												}}>
												{getChannelIcon(channel.name)}
											</Avatar>
											<Box sx={{ flex: 1 }}>
												<Typography variant="subtitle2" fontWeight={600}>
													{channel.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{channel.count} {t('spendPlan.paymentReport.payments')}
												</Typography>
											</Box>
											<Typography variant="subtitle1" fontWeight={700} color="primary">
												{formatCurrency({
													value: channel.amount,
													currency: defaultCurrency,
													notation: 'compact',
												})}
											</Typography>
										</Stack>
									</Card>
								</Grid>
							))}
						</Grid>
					</Paper>
				</Grid>
			</Grid>
		</Box>
	);

	return (
		<Box>
			{/* Header */}
			<HeaderListingPage
				icon={IconPayments}
				title={t('spendPlan.paymentReport.title')}
				subtitle={t('spendPlan.paymentReport.subtitle')}
				variant="activity"
			/>

			{/* Content */}
			<Box sx={{ p: 3 }}>
				{/* Summary Cards */}
				{renderSummaryCards()}

				{/* Tabs */}
				<Paper sx={{ borderRadius: 2 }}>
					<Tabs
						value={tabValue}
						onChange={handleTabChange}
						sx={{
							borderBottom: 1,
							borderColor: 'divider',
							px: 2,
							'& .MuiTab-root': {
								textTransform: 'none',
								fontWeight: 500,
								minHeight: 56,
							},
						}}>
						<Tab
							icon={<IconReceipt sx={{ fontSize: 20 }} />}
							iconPosition="start"
							label={t('spendPlan.paymentReport.tabPayments')}
						/>
						<Tab
							icon={<IconTimeline sx={{ fontSize: 20 }} />}
							iconPosition="start"
							label={t('spendPlan.paymentReport.tabAnalytics')}
						/>
					</Tabs>

					<Box sx={{ px: 3 }}>
						<TabPanel value={tabValue} index={0}>
							{renderPaymentsTab()}
						</TabPanel>
						<TabPanel value={tabValue} index={1}>
							{renderAnalyticsTab()}
						</TabPanel>
					</Box>
				</Paper>
			</Box>
		</Box>
	);
};
